import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { UserAdminListUserUpstream } from 'src/types/user';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';
import { mapUserAdminListUserToProps } from 'src/components/app/context/UserContext/helper';

interface DataResponse {
  results: UserAdminListUserUpstream[];
  count: number;
  next: string;
  previous: string;
}

const useUserAdminQuery = (
  searchString: string,
  pageNo: number,
  searchFilters: Record<string, string>[][],
  pageSize: number,
) => {
  const { t } = useTranslation('common');
  const { currentSurvey } = useUserContext();
  const endPoint = apiPaths.ADMIN.USER;

  return useQuery<any>({
    cacheTime: 0,
    queryKey: [
      endPoint,
      currentSurvey?.id,
      pageNo,
      pageSize,
      searchFilters,
      searchString,
    ],
    queryFn: ({ signal }) =>
      HttpService.get(
        endPoint,
        {
          search: searchString,
          page: pageNo,
          pageSize: pageSize,
          filter: JSON.stringify(searchFilters),
        },
        true,
        { signal },
      )
        .then(({ data }: AxiosResponse<DataResponse>) => {
          const { results, count, next, previous } = data;
          return {
            users: results.map(mapUserAdminListUserToProps),
            count,
            next,
            previous,
          };
        })
        .catch(({ response }) => {
          const errorMsg = response?.data?.detail || t('networkError');
          return Promise.reject(errorMsg || 'An unknown error occurred.');
        }),
  });
};

export default useUserAdminQuery;
